import { useEffect, useState } from "react";
import { listCoupons, listWallet } from "@/lib/pr25/couponEngine";
import { getSessionId } from "@/lib/session";

const SEEN_KEY = "ss2026_wallet_seen_at";

/**
 * Pure-derivation discovery counter for the wallet menu entry.
 * Counts saved coupons (still valid) + active coupons not yet saved that
 * were created after the user's last visit to the wallet surface.
 * No new polling/realtime/tables — reads existing localStorage state.
 */
export function useWalletNewCount(): number {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const compute = () => {
      if (typeof window === "undefined") return 0;
      const sid = getSessionId();
      const seenAt = Number(localStorage.getItem(SEEN_KEY) ?? 0);
      const now = Date.now();
      const coupons = listCoupons();
      const wallet = listWallet().filter((w) => w.session_id === sid);
      const savedIds = new Set(wallet.map((w) => w.coupon_id));

      const freshSaved = wallet.filter(
        (w) => w.state === "saved" && w.saved_at > seenAt,
      ).length;
      const freshOffers = coupons.filter(
        (c) =>
          c.created_at > seenAt &&
          c.expires_at > now &&
          c.starts_at <= now &&
          !savedIds.has(c.coupon_id),
      ).length;
      return freshSaved + freshOffers;
    };
    setCount(compute());

    const onStorage = (e: StorageEvent) => {
      if (!e.key || e.key === SEEN_KEY || e.key.startsWith("ss2026_pr25_")) {
        setCount(compute());
      }
    };
    const onFocus = () => setCount(compute());
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  return count;
}

export function markWalletSeen() {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(SEEN_KEY, String(Date.now()));
  } catch {
    /* noop */
  }
}